import moment from "moment";
import {
  EHotelServiceRequestType,
  ELineItemStatus,
} from "../../constants";
import { EServiceRequestType } from "../../types/global.types";
import { easternWallClockToIso } from "../../utils/date.utils";
import { IInvoiceLineItem } from "./invoices.api.types";

const HAS_ZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;

const parseStrict = (value: unknown, format: string): moment.Moment | undefined => {
  if (!value) return undefined;

  for (const candidate of [format, moment.ISO_8601]) {
    const parsed = moment(String(value).trim(), candidate as any, true);
    if (parsed.isValid()) return parsed;
  }

  return undefined;
};

const toEasternIso = (value: unknown, format: string): string | undefined => {
  if (!value) return undefined;

  const raw = String(value).trim();
  if (HAS_ZONE_PATTERN.test(raw)) {
    const instant = moment(raw, moment.ISO_8601, true);
    return instant.isValid() ? instant.toISOString() : undefined;
  }

  const parsed = parseStrict(raw, format);
  return parsed ? easternWallClockToIso(parsed.format("YYYY-MM-DDTHH:mm:ss")) : undefined;
};

const toCalendarDay = (value: unknown, format: string): string | undefined =>
  parseStrict(value, format)?.format("YYYY-MM-DD");

export const fromReviewStatus = (
  reviewStatus: unknown
): ELineItemStatus | undefined => {
  const value = String(reviewStatus ?? "").trim().toUpperCase();
  if (value === "APPROVED") return ELineItemStatus.APPROVED;
  if (value === "REJECTED") return ELineItemStatus.REJECTED;
  if (value === "PENDING") return ELineItemStatus.PENDING;
  return undefined;
};

const MEAL_TYPES: string[] = Object.values(EHotelServiceRequestType);

export const mapMealScanToLineItem = (row: any): IInvoiceLineItem => {
  const rawType = String(row?.requestType ?? "").toUpperCase();
  const isMeal = MEAL_TYPES.includes(rawType);

  const movementDate = toCalendarDay(row?.date, "DD/MM/YYYY");
  const scannedOn =
    toEasternIso(row?.CheckInDate, "MM/DD/YYYY HH:mm:ss") ?? movementDate;

  return {
    id: String(row?.id ?? ""),
    tmId: String(row?.tmId ?? ""),
    crewName: row?.tmName || undefined,
    movementId: undefined,
    movementDate,
    requestType: isMeal ? EServiceRequestType.MEAL_SCAN : rawType,
    mealType: isMeal ? rawType : undefined,
    scannedOn: scannedOn ?? "",
    vendorId: String(row?.vendorId ?? ""),
    createdOn: String(row?.createdOn ?? ""),
    updatedOn: String(row?.updatedOn ?? ""),
    greeterName: row?.vendorEmpName || undefined,
    itemCost: undefined,
    lineStatus: fromReviewStatus(row?.reviewStatus),
    reviewedOn: row?.reviewedOn || undefined,
    rejectionReason: row?.reviewReason || undefined,
    rejectionComments: row?.reviewNotes || undefined,
    isNewlyAdded: Boolean(row?.isManuallyAdded),
    superAdminApprovalCreatedOn: row?.superAdminApprovalCreatedOn || undefined,
    rawInvoiceId: row?.invoiceId || undefined,
  };
};

export const mapAssignmentToLineItem = (row: any): IInvoiceLineItem => {
  const arrivalDate = toCalendarDay(row?.arrivalDate, "MM/DD/YYYY");

  const arrivalDateTime =
    row?.arrivalDate && row?.arrivalTime
      ? toEasternIso(
        `${row.arrivalDate} ${row.arrivalTime}`,
        "MM/DD/YYYY HH:mm:ss"
      )
      : arrivalDate;

  return {
    id: String(row?.id ?? ""),
    tmId: String(row?.tmId ?? ""),
    crewName: row?.tmName || undefined,
    movementId: undefined,
    movementDate: arrivalDate,
    requestType: String(row?.requestType ?? ""),
    scannedOn: arrivalDateTime ?? "",
    vendorId: String(row?.vendorId ?? ""),
    createdOn: String(row?.createdOn ?? ""),
    updatedOn: String(row?.updatedOn ?? ""),
    greeterName: row?.vendorEmpName || undefined,
    itemCost: undefined,
    lineStatus: fromReviewStatus(row?.reviewStatus),
    reviewedOn: row?.reviewedOn || undefined,
    rejectionReason: row?.reviewReason || undefined,
    rejectionComments: row?.reviewNotes || undefined,
    isNewlyAdded: Boolean(row?.isManuallyAdded),
    superAdminApprovalCreatedOn: row?.superAdminApprovalCreatedOn || undefined,
    rawInvoiceId: row?.invoiceId || undefined,
  };
};

export const collectManualScanLineItems = (
  mealResponse: any,
  assignmentResponse: any,
  invoiceId?: string
): IInvoiceLineItem[] => {
  const rowsOf = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const belongsToInvoice = (row: any) =>
    !invoiceId || !row?.invoiceId || String(row.invoiceId) === String(invoiceId);

  const isManualForThisInvoice = (row: any) =>
    row?.isManuallyAdded && belongsToInvoice(row);

  const meals = rowsOf(mealResponse)
    .filter(isManualForThisInvoice)
    .map(mapMealScanToLineItem);

  const assignments = rowsOf(assignmentResponse)
    .filter(isManualForThisInvoice)
    .map(mapAssignmentToLineItem);

  return [...meals, ...assignments];
};
